<template>
    <div class="consumption-heatmap">
        <VHeader page="Consumption Heatmap"></VHeader>

        <div class="consumption-heatmap-filters">
            <div class="consumption-heatmap-filters__field">
                <label>Begin</label>
                <input type="date" v-model="filters.begin" />
            </div>
            <div class="consumption-heatmap-filters__field">
                <label>End</label>
                <input type="date" v-model="filters.end" />
            </div>
            <div class="consumption-heatmap-filters__field">
                <label>Policy Label</label>
                <select v-model="filters.policyLabel" class="consumption-heatmap-filters__select">
                    <option v-if="!labels.length" :value="''" disabled>No labels in range</option>
                    <option v-for="lbl in labels" :key="lbl" :value="lbl">{{ lbl }}</option>
                </select>
            </div>
            <div class="consumption-heatmap-filters__field">
                <label>Bundle ID</label>
                <VInput v-model="filters.bundleId" placeholder="com.example.app"></VInput>
            </div>
            <div class="consumption-heatmap-filters__field">
                <label>Geo</label>
                <VInput v-model="filters.geo" placeholder="US"></VInput>
            </div>
            <div class="consumption-heatmap-filters__field">
                <label>Product ID</label>
                <VInput v-model="filters.productId" placeholder=""></VInput>
            </div>
            <div class="consumption-heatmap-filters__field">
                <label>Pay Type</label>
                <VInput v-model="filters.payType" placeholder="intro"></VInput>
            </div>
            <VButton theme="dark-blue" @click="load">Load</VButton>
        </div>

        <div class="consumption-heatmap-groupby">
            <label class="consumption-heatmap-groupby__title">Group by:</label>
            <label v-for="dim in availableGroupBy" :key="dim" class="consumption-heatmap-groupby__chk">
                <input type="checkbox" :value="dim" v-model="filters.groupBy" />
                {{ dim }}
            </label>
        </div>

        <template v-if="loaded">
            <div v-if="!rows.length" class="consumption-heatmap-empty">
                No data for the selected filters.
            </div>

            <div v-else-if="canRenderMatrix" class="consumption-heatmap-matrix">
                <table class="consumption-heatmap-table">
                    <thead>
                        <tr>
                            <th class="consumption-heatmap-table__rowdim">{{ matrixRowDim }}</th>
                            <th
                                v-for="col in matrix.colKeys"
                                :key="col"
                                class="consumption-heatmap-table__col"
                            >
                                {{ col }}%
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="row in matrix.rowKeys" :key="row">
                            <td class="consumption-heatmap-table__rowlabel">{{ row }}</td>
                            <td
                                v-for="col in matrix.colKeys"
                                :key="col"
                                class="consumption-heatmap-table__cell"
                                :class="cellClass(matrix.cells[`${row}|${col}`])"
                                :style="cellStyle(matrix.cells[`${row}|${col}`])"
                                :title="cellTooltip(matrix.cells[`${row}|${col}`])"
                            >
                                <template v-if="cellMature(matrix.cells[`${row}|${col}`]) >= MATURE_THRESHOLD">
                                    {{ formatPct(matrix.cells[`${row}|${col}`].win_rate) }}
                                    <span class="consumption-heatmap-table__cell-n">
                                        ({{ cellMature(matrix.cells[`${row}|${col}`]) }}/{{ matrix.cells[`${row}|${col}`].total }})
                                    </span>
                                </template>
                                <template v-else-if="matrix.cells[`${row}|${col}`]">
                                    <span class="consumption-heatmap-table__cell-low">
                                        — ({{ cellMature(matrix.cells[`${row}|${col}`]) }}/{{ matrix.cells[`${row}|${col}`].total }})
                                    </span>
                                </template>
                                <template v-else>—</template>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div v-else class="consumption-heatmap-list">
                <table class="consumption-heatmap-table">
                    <thead>
                        <tr>
                            <th v-for="dim in filters.groupBy" :key="dim">{{ dim }}</th>
                            <th>Total</th>
                            <th>Declined</th>
                            <th>Refunded</th>
                            <th>Pending</th>
                            <th>Decline Rate</th>
                            <th>Refunded $</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(r, idx) in rows" :key="idx">
                            <td v-for="dim in filters.groupBy" :key="dim">{{ r[dim] }}</td>
                            <td>{{ r.total }}</td>
                            <td>{{ r.won }}</td>
                            <td>{{ r.lost }}</td>
                            <td>{{ r.pending }}</td>
                            <td>{{ formatPct(r.win_rate) }}</td>
                            <td>{{ formatMoney(r.lost_revenue) }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </template>

        <template v-if="isLoading">
            <VLoadingRow height="200px"></VLoadingRow>
        </template>
    </div>
</template>

<script>
    import VButton from '../components/VButton';
    import VHeader from '../components/VHeader';
    import VInput from '../components/VInput';
    import VLoadingRow from '../components/VLoadingRow';
    import apiConsumption from '../api/apiConsumption';

    const GROUP_BY_WHITELIST = [
        'bundle_id',
        'geo',
        'product_id',
        'pay_type',
        'pay_number',
        'subscription_type',
        'discount_type',
        'consumption_percentage',
        'iap_type',
        'storefront',
        'transaction_reason',
        'offer_type',
        'offer_discount_type',
        'in_app_ownership_type',
    ];

    const MATURE_THRESHOLD = 10;

    export default {
        name: 'ConsumptionHeatmap',
        components: {
            VButton,
            VHeader,
            VInput,
            VLoadingRow,
        },
        data() {
            const now = new Date();
            const end = now.toISOString().slice(0, 10);
            const beginDate = new Date(now);
            beginDate.setDate(beginDate.getDate() - 7);
            const begin = beginDate.toISOString().slice(0, 10);

            return {
                availableGroupBy: GROUP_BY_WHITELIST,
                MATURE_THRESHOLD,
                filters: {
                    begin,
                    end,
                    policyLabel: '',
                    groupBy: ['consumption_percentage'],
                    bundleId: '',
                    geo: '',
                    productId: '',
                    payType: '',
                },
                labels: [],
                rows: [],
                isLoading: false,
                loaded: false,
            };
        },
        created() {
            this.loadLabels();
        },
        computed: {
            matrixRowDim() {
                return this.filters.groupBy.find((d) => d !== 'consumption_percentage') || this.filters.groupBy[0];
            },
            canRenderMatrix() {
                return (
                    this.filters.groupBy.includes('consumption_percentage') &&
                    this.filters.groupBy.length >= 2
                );
            },
            matrix() {
                const rowDim = this.matrixRowDim;
                const colDim = 'consumption_percentage';
                const rowKeysSet = new Set();
                const colKeysSet = new Set();
                const cells = {};
                const rowTotals = {};
                for (const r of this.rows) {
                    const rk = r[rowDim];
                    const ck = r[colDim];
                    rowKeysSet.add(rk);
                    colKeysSet.add(ck);
                    cells[`${rk}|${ck}`] = r;
                    rowTotals[rk] = (rowTotals[rk] || 0) + (r.total || 0);
                }
                return {
                    rowKeys: [...rowKeysSet].sort((a, b) => (rowTotals[b] || 0) - (rowTotals[a] || 0)),
                    colKeys: [...colKeysSet].sort((a, b) => Number(a) - Number(b)),
                    cells,
                };
            },
            // Auto-scale color range across mature cells only. With realistic
            // refund rates in the 5-15% band a fixed 0..100 scale would render
            // every cell in the orange zone, masking small differences.
            rateBounds() {
                let min = Infinity;
                let max = -Infinity;
                for (const r of this.rows) {
                    const mature = (r.won || 0) + (r.lost || 0);
                    if (mature < MATURE_THRESHOLD) continue;
                    const rate = r.win_rate || 0;
                    if (rate < min) min = rate;
                    if (rate > max) max = rate;
                }
                if (!Number.isFinite(min) || !Number.isFinite(max)) {
                    return null;
                }
                // Guard against a tiny span — spreading 1-2pp across the full
                // red-green range would over-dramatize noise.
                if (max - min < 0.02) {
                    return {min, max: min + 0.02};
                }
                return {min, max};
            },
        },
        methods: {
            loadLabels() {
                apiConsumption.getLabels({
                    begin: this.filters.begin,
                    end: this.filters.end,
                }).then((res) => {
                    const data = res.data;
                    this.labels = data.labels || [];
                    if (this.labels.length && !this.filters.policyLabel) {
                        this.filters.policyLabel = this.labels[0];
                    }
                }).catch(() => {
                    this.$toastr.longError('Failed to load policy labels');
                });
            },
            load() {
                if (!this.filters.policyLabel) {
                    this.$toastr.longError('Pick a policy label first');
                    return;
                }
                if (!this.filters.groupBy.length) {
                    this.$toastr.longError('Pick at least one group_by dimension');
                    return;
                }
                this.isLoading = true;
                apiConsumption.getHeatmap({
                    begin: this.filters.begin,
                    end: this.filters.end,
                    policyLabel: this.filters.policyLabel,
                    groupBy: this.filters.groupBy,
                    bundleId: this.filters.bundleId,
                    geo: this.filters.geo,
                    productId: this.filters.productId,
                    payType: this.filters.payType,
                }).then((res) => {
                    const data = res.data;
                    this.rows = data.rows || [];
                    this.loaded = true;
                }).catch(() => {
                    this.$toastr.longError('Failed to load heatmap');
                }).finally(() => {
                    this.isLoading = false;
                });
            },
            formatPct(v) {
                if (v == null) return '—';
                return `${(v * 100).toFixed(1)}%`;
            },
            formatMoney(v) {
                if (v == null) return '—';
                return `$${Number(v).toFixed(2)}`;
            },
            cellMature(r) {
                if (!r) return 0;
                return (r.won || 0) + (r.lost || 0);
            },
            cellClass(r) {
                if (this.cellMature(r) < MATURE_THRESHOLD) {
                    return 'consumption-heatmap-table__cell--low';
                }
                return '';
            },
            cellStyle(r) {
                if (!r) return 'background: #f7f7f7;';
                if (this.cellMature(r) < MATURE_THRESHOLD) {
                    return 'background: #f0f0f0; color: rgba(0,0,0,0.4);';
                }
                const bounds = this.rateBounds;
                const rate = r.win_rate || 0;
                let t;
                if (!bounds || bounds.max === bounds.min) {
                    t = 0.5;
                } else {
                    t = (rate - bounds.min) / (bounds.max - bounds.min);
                    t = Math.max(0, Math.min(1, t));
                }
                // High win_rate = high share of declined refunds (we kept the
                // money) = green. Low win_rate = Apple issued the refund more
                // often = red. Scaled to the observed [min..max] band so small
                // differences are visible instead of all collapsing to orange.
                const hue = t * 120;
                return `background: hsl(${hue}, 70%, 80%);`;
            },
            cellTooltip(r) {
                if (!r) return 'no data';
                const mature = this.cellMature(r);
                const note = mature < MATURE_THRESHOLD ? ' (too few mature outcomes)' : '';
                return `Declined: ${r.won} / Refunded: ${r.lost} / Pending: ${r.pending} / Total: ${r.total} / Refunded $: ${this.formatMoney(r.lost_revenue)}${note}`;
            },
        },
    };
</script>

<style scoped lang="scss">
    .consumption-heatmap {
        padding: 0 24px 24px;

        &-filters {
            display: flex;
            align-items: flex-end;
            gap: 16px;
            margin-bottom: 16px;
            flex-wrap: wrap;

            &__hint {
                font-size: 10px;
                color: rgba($dark-blue-color, 0.4);
                font-weight: 400;
                margin-left: 2px;
            }

            &__field {
                display: flex;
                flex-direction: column;
                gap: 4px;

                label {
                    font-size: 12px;
                    color: rgba($dark-blue-color, 0.6);
                    font-weight: 500;
                    font-family: Quicksand;
                }

                input[type="date"],
                &__select,
                select {
                    height: 28px;
                    padding: 0 8px;
                    border: 1px solid $primary-color;
                    border-radius: 4px;
                    font-size: 13px;
                    font-family: Quicksand;
                    color: $dark-blue-color;
                    outline: none;
                    background: #fff;

                    &:focus {
                        @include blue-shadow;
                    }
                }
            }
        }

        &-groupby {
            display: flex;
            flex-wrap: wrap;
            gap: 12px;
            align-items: center;
            margin-bottom: 24px;
            padding: 12px;
            background: #fff;
            border-radius: 8px;
            @include light-shadow;

            &__title {
                font-size: 12px;
                color: rgba($dark-blue-color, 0.6);
                text-transform: uppercase;
                letter-spacing: 0.05em;
                font-family: Quicksand;
                font-weight: 600;
            }

            &__chk {
                font-size: 13px;
                color: $dark-blue-color;
                font-family: Quicksand;
                display: inline-flex;
                align-items: center;
                gap: 4px;
                cursor: pointer;
            }
        }

        &-empty {
            padding: 24px;
            text-align: center;
            color: rgba($dark-blue-color, 0.6);
            background: #fff;
            border-radius: 10px;
            @include light-shadow;
        }

        &-table {
            border-collapse: collapse;
            width: 100%;
            background: #fff;
            border-radius: 10px;
            overflow: hidden;
            @include dark-shadow;

            th, td {
                padding: 8px 12px;
                font-size: 12px;
                font-family: Quicksand;
                color: $dark-blue-color;
                border-bottom: 1px solid rgba($primary-color, 0.3);
                text-align: right;
            }

            th {
                font-weight: 700;
                font-family: Poppins;
                text-transform: uppercase;
                letter-spacing: 0.05em;
                background: rgba($primary-color, 0.08);
            }

            &__rowdim {
                text-align: left;
            }

            &__rowlabel {
                text-align: left;
                font-weight: 600;
            }

            &__cell {
                text-align: center;
                font-weight: 600;

                &-n {
                    font-weight: 400;
                    color: rgba($dark-blue-color, 0.6);
                    margin-left: 4px;
                    font-size: 11px;
                }

                &-low {
                    font-weight: 400;
                    color: rgba($dark-blue-color, 0.4);
                    font-style: italic;
                }
            }
        }
    }
</style>
